import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  ApiError,
  type PublicBookletResponseDto,
  type PublicBookletDetailResponseDto,
  type PublicBookletSpotDetailResponseDto,
  type BookletLikesResponseDto,
} from "@tabipla/shared";

// Query key factory
const discoverKeys = {
  all: ["mekke-discover"] as const,
  booklets: () => [...discoverKeys.all, "booklets"] as const,
  bookletList: (params: { limit?: number; offset?: number }) =>
    [...discoverKeys.booklets(), params] as const,
  timeline: () => [...discoverKeys.all, "timeline"] as const,
  timelineList: (params: { limit?: number; offset?: number }) =>
    [...discoverKeys.timeline(), params] as const,
  publicBooklet: (id: string) => [...discoverKeys.all, "public-booklet", id] as const,
  publicBookletSpot: (bookletId: string, spotId: string) =>
    [...discoverKeys.all, "public-booklet-spot", bookletId, spotId] as const,
  bookletLikes: (id: string) => [...discoverKeys.all, "booklet-likes", id] as const,
};

interface UsePublicBookletsOptions {
  limit?: number;
  offset?: number;
}

// Fetch public booklets (new arrivals)
export function usePublicBooklets(options: UsePublicBookletsOptions = {}) {
  const { limit = 20, offset = 0 } = options;

  return useQuery({
    queryKey: discoverKeys.bookletList({ limit, offset }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (limit) params.set("limit", limit.toString());
      if (offset) params.set("offset", offset.toString());

      const endpoint = `/api/mekke/discover/booklets${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await apiClient.get<PublicBookletResponseDto[]>(endpoint);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch public booklets");
      }
      return response.data ?? [];
    },
  });
}

// Fetch a single public booklet by ID
export function usePublicBooklet(id: string | undefined) {
  return useQuery({
    queryKey: discoverKeys.publicBooklet(id ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<PublicBookletDetailResponseDto>(
        `/api/mekke/booklets/public/${id}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch public booklet");
      }
      return response.data!;
    },
    enabled: !!id,
  });
}

// Fetch a spot within a public booklet
export function usePublicBookletSpot(bookletId: string | undefined, spotId: string | undefined) {
  return useQuery({
    queryKey: discoverKeys.publicBookletSpot(bookletId ?? "", spotId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<PublicBookletSpotDetailResponseDto>(
        `/api/mekke/booklets/public/${bookletId}/${spotId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch spot");
      }
      return response.data!;
    },
    enabled: !!bookletId && !!spotId,
  });
}

interface UseTimelineFeedOptions {
  limit?: number;
  offset?: number;
  enabled?: boolean;
}

// Fetch timeline feed (booklets from followed users)
export function useTimelineFeed(options: UseTimelineFeedOptions = {}) {
  const { limit = 20, offset = 0, enabled = true } = options;

  return useQuery({
    queryKey: discoverKeys.timelineList({ limit, offset }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (limit) params.set("limit", limit.toString());
      if (offset) params.set("offset", offset.toString());

      const endpoint = `/api/mekke/discover/timeline${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await apiClient.get<PublicBookletResponseDto[]>(endpoint);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch timeline");
      }
      return response.data ?? [];
    },
    enabled,
  });
}

// Fetch users who liked a booklet
export function useBookletLikes(bookletId: string | undefined) {
  return useQuery({
    queryKey: discoverKeys.bookletLikes(bookletId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<BookletLikesResponseDto>(
        `/api/mekke/booklets/public/${bookletId}/likes`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch booklet likes");
      }
      return response.data!;
    },
    enabled: !!bookletId,
  });
}

// Like a booklet
export function useLikeBooklet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookletId: string) => {
      const response = await apiClient.post<{ liked: boolean; likeCount: number }>(
        `/api/mekke/booklets/${bookletId}/like`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to like booklet");
      }
      return response.data!;
    },
    onSuccess: (_data, bookletId) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: discoverKeys.publicBooklet(bookletId) });
      queryClient.invalidateQueries({ queryKey: discoverKeys.bookletLikes(bookletId) });
      queryClient.invalidateQueries({ queryKey: discoverKeys.booklets() });
      queryClient.invalidateQueries({ queryKey: discoverKeys.timeline() });
    },
  });
}

// Unlike a booklet
export function useUnlikeBooklet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookletId: string) => {
      const response = await apiClient.delete<{ liked: boolean; likeCount: number }>(
        `/api/mekke/booklets/${bookletId}/like`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to unlike booklet");
      }
      return response.data!;
    },
    onSuccess: (_data, bookletId) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: discoverKeys.publicBooklet(bookletId) });
      queryClient.invalidateQueries({ queryKey: discoverKeys.bookletLikes(bookletId) });
      queryClient.invalidateQueries({ queryKey: discoverKeys.booklets() });
      queryClient.invalidateQueries({ queryKey: discoverKeys.timeline() });
    },
  });
}
